import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { UsersAPI, type UserDto } from "../../api/usersApi";

interface Props {
  /** Откуда открыли: сделка с доски или заказ из списка в настройках Kaspi */
  dealId?: number;
  orderId?: number;
  dealNumber?: string;
  onClose: () => void;
  onDone: () => void;
}

interface KaspiOrderRow {
  id: number;
  code: string;
  status: string;
  totalPrice: number;
  dealId: number | null;
  dealNumber?: string | null;
}

const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const box: React.CSSProperties = { background: "#111827", border: "1px solid #1f2937", borderRadius: 12, padding: 20, width: "100%", maxWidth: 440, color: "#e5e7eb", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" };
const lab: React.CSSProperties = { display: "block", fontSize: 12, color: "#9ca3af", margin: "14px 0 6px" };
const inp: React.CSSProperties = { width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid #374151", background: "#0b1220", color: "#f9fafb", fontSize: 14, boxSizing: "border-box" };
const btn: React.CSSProperties = { padding: "9px 16px", borderRadius: 8, border: "1px solid #374151", background: "#1f2937", color: "#e5e7eb", fontSize: 14, cursor: "pointer" };
const btnMain: React.CSSProperties = { ...btn, background: "#16a34a", border: "1px solid #16a34a", color: "#fff", fontWeight: 600 };

export default function KaspiIssueModal({ dealId, orderId, dealNumber, onClose, onDone }: Props) {
  const [order, setOrder] = useState<KaspiOrderRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [issuerId, setIssuerId] = useState<number | "">("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadOrder = async () => {
    const rows: KaspiOrderRow[] = (await api.get("/kaspi/orders")).data ?? [];
    setOrder(rows.find((r) => (orderId ? r.id === orderId : r.dealId === dealId)) ?? null);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadOrder();
        let list: UserDto[] = [];
        try { list = (await UsersAPI.getAll()).filter((u) => u.isActive && !u.dismissedAt); } catch { /* нет прав на список — выдаёт сам вошедший */ }
        const me = await UsersAPI.me().catch(() => null);
        if (me && !list.some((u) => u.id === me.id)) list = [me, ...list];
        setUsers(list);
        if (me) setIssuerId(me.id);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch { /* причину покажет окно ошибки из api/axios.ts */ } finally { setBusy(false); }
  };

  const sendCode = () => run(async () => {
    await api.post(`/kaspi/orders/${order!.id}/complete`, {});
    setCodeSent(true);
  });

  const issue = () => run(async () => {
    await api.post(`/kaspi/orders/${order!.id}/complete`, { code: code.trim(), issuerId: Number(issuerId) });
    onDone();
  });

  const accept = () => run(async () => {
    await api.post(`/kaspi/orders/${order!.id}/accept`);
    await loadOrder();
  });

  const status = order?.status;
  const title = `Выдача заказа Kaspi ${order?.code ?? ""}`.trim();

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={box}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#f9fafb" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
          {(order?.dealNumber ?? dealNumber) ? `Сделка ${order?.dealNumber ?? dealNumber}` : ""}
          {order ? ` · ${Math.round(order.totalPrice).toLocaleString("ru-RU")} ₸` : ""}
        </div>

        {loading && <div style={{ marginTop: 18, color: "#9ca3af" }}>Загрузка…</div>}

        {!loading && !order && (
          <div style={{ marginTop: 18, color: "#fbbf24" }}>Заказ Kaspi по этой сделке не найден.</div>
        )}

        {!loading && order && status === "COMPLETED" && (
          <div style={{ marginTop: 18, color: "#86efac" }}>Заказ уже выдан.</div>
        )}

        {!loading && order && status === "APPROVED_BY_BANK" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ color: "#fbbf24", marginBottom: 12 }}>Заказ ещё не принят в Kaspi. Сначала примите его.</div>
            <button style={btnMain} disabled={busy} onClick={accept}>Принять заказ</button>
          </div>
        )}

        {!loading && order && status === "ACCEPTED_BY_MERCHANT" && (
          <>
            <label style={lab}>Кто выдаёт заказ (за выдачу начисляется оплата)</label>
            <select style={inp} value={issuerId} onChange={(e) => setIssuerId(e.target.value ? Number(e.target.value) : "")} disabled={busy}>
              <option value="">— выберите сотрудника —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.lastName} {u.firstName}</option>)}
            </select>

            <label style={lab}>Код покупателя из приложения Kaspi</label>
            {!codeSent && (
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                Нажмите «Отправить код», Kaspi пришлёт код покупателю. Если код у покупателя уже есть, введите его ниже.
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input style={inp} inputMode="numeric" autoComplete="off" placeholder="Код" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))} disabled={busy} />
              <button style={btn} disabled={busy} onClick={sendCode}>{codeSent ? "Отправить снова" : "Отправить код"}</button>
            </div>
            {codeSent && <div style={{ fontSize: 12, color: "#86efac", marginTop: 6 }}>Код отправлен покупателю в приложении Kaspi.</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
              <button style={btn} disabled={busy} onClick={onClose}>Отмена</button>
              <button style={{ ...btnMain, opacity: !code.trim() || !issuerId ? 0.5 : 1 }} disabled={busy || !code.trim() || !issuerId} onClick={issue}>Выдать заказ</button>
            </div>
          </>
        )}

        {!loading && order && status !== "ACCEPTED_BY_MERCHANT" && status !== "APPROVED_BY_BANK" && status !== "COMPLETED" && (
          <div style={{ marginTop: 18, color: "#fbbf24" }}>Заказ нельзя выдать: статус в Kaspi «{status}».</div>
        )}

        {!(order && status === "ACCEPTED_BY_MERCHANT") && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button style={btn} onClick={onClose}>Закрыть</button>
          </div>
        )}
      </div>
    </div>
  );
}
